import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useLocale } from '../i18n';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="container-site flex min-h-[75vh] flex-col items-start justify-center pt-28 pb-16">
      <meta name="robots" content="noindex" />
      <div className="max-w-xl">{children}</div>
    </div>
  );
}

/**
 * Double opt-in confirmation. The email links here and the page POSTs the token, so link
 * scanners that merely fetch the URL don't confirm subscriptions on someone's behalf.
 */
export function NewsletterConfirmPage() {
  const { t, to } = useLocale();
  const n = t.newsletter;
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const fired = useRef(false);
  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ status: 'confirmed' | 'already' }>('/newsletter/confirm', { token }),
  });
  useEffect(() => {
    if (fired.current || !token) return;
    fired.current = true;
    mutation.mutate();
  }, [token, mutation]);

  return (
    <Shell>
      <title>{n.confirmTitle}</title>
      {!token ? (
        <>
          <XCircle className="size-10 text-danger" />
          <h1 className="mt-5 text-h2 font-black">{n.missingLink}</h1>
          <p className="mt-3 font-light text-graphite">{n.missingLinkText}</p>
        </>
      ) : mutation.isPending || mutation.isIdle ? (
        <p className="flex items-center gap-3 text-body">
          <Loader2 className="size-5 animate-spin text-primary" /> {n.confirming}
        </p>
      ) : mutation.isError ? (
        <>
          <XCircle className="size-10 text-danger" />
          <h1 className="mt-5 text-h2 font-black">{n.failed}</h1>
          <p className="mt-3 font-light text-graphite">
            {t.states.apiError ?? errorMessage(mutation.error)}
          </p>
          <Link
            to={to('/')}
            className="mt-8 inline-flex h-12 items-center rounded-lg bg-ink px-6 font-bold text-canvas hover:bg-primary"
          >
            {n.backHome}
          </Link>
        </>
      ) : (
        <>
          <CheckCircle2 className="size-10 text-success" />
          <h1 className="mt-5 text-h2 font-black">
            {mutation.data?.status === 'already' ? n.already : n.subscribed}
          </h1>
          <p className="mt-3 font-light text-graphite">{n.thanks}</p>
          <Link
            to={to('/')}
            className="mt-8 inline-flex h-12 items-center rounded-lg bg-ink px-6 font-bold text-canvas hover:bg-primary"
          >
            {n.backSite}
          </Link>
        </>
      )}
    </Shell>
  );
}

export function UnsubscribePage() {
  const { t } = useLocale();
  const n = t.newsletter;
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const campaign = params.get('c') ?? undefined;
  const [done, setDone] = useState(false);
  const mutation = useMutation({
    mutationFn: () => api.post('/newsletter/unsubscribe', { token, c: campaign }),
    onSuccess: () => setDone(true),
  });
  return (
    <Shell>
      <title>{n.unsubscribeTitle}</title>
      {done ? (
        <>
          <CheckCircle2 className="size-10 text-success" />
          <h1 className="mt-5 text-h2 font-black">{n.unsubscribed}</h1>
          <p className="mt-3 font-light text-graphite">{n.unsubscribedText}</p>
        </>
      ) : (
        <>
          <h1 className="text-h2 font-black">{n.unsubscribeQuestion}</h1>
          <p className="mt-3 font-light text-graphite">{n.unsubscribeText}</p>
          {!token || token === 'preview' ? (
            <p className="mt-6 text-caption text-danger">{n.previewLink}</p>
          ) : (
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="mt-8 inline-flex h-12 items-center gap-2 rounded-lg bg-ink px-6 font-bold text-canvas hover:bg-primary disabled:opacity-60"
            >
              {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
              {n.unsubscribe}
            </button>
          )}
          {mutation.isError && (
            <p role="alert" className="mt-4 text-caption text-danger">
              {t.states.apiError ?? errorMessage(mutation.error)}
            </p>
          )}
        </>
      )}
    </Shell>
  );
}
